/**
 * @license
 * Copyright (C) 2024 Guido Berhoerster <guido+voxit@berhoerster.name>
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

/**
 * @document doc/reference/voxit-conversation.md
 * @document doc/reference/voxit-topic.md
 * @document doc/reference/voxit-description.md
 * @document doc/reference/voxit-statement.md
 * @document doc/reference/voxit-login-button.md
 * @document doc/reference/voxit-vote-button.md
 * @document doc/reference/voxit-submit-button.md
 * @document doc/reference/voxit-notifications-checkbox.md
 * @module voxit-web-components
 */

import * as voxitWebClient from "@voxit/voxit-web-client";

/**
 * Minimum interval in ms for polling for new statements and results.
 * @type {number}
 */
const POLL_INTERVAL_MIN = 8000;
/**
 * Maximum interval in ms for polling for new statements and results.
 * @type {number}
 */
const POLL_INTERVAL_MAX = 12000;

/**
 * @param {any} value
 * @returns {Error}
 */
function ensureError(value) {
    return value instanceof Error ? value : new Error(String(value));
}

/**
 * The VoxitElementConnectedEvent class representa a
 * voxitelementconnected event.  A voxitelementconnected event is
 * dispatched by a custom element from the Voxit Web Components library
 * each time it is added to the document.
 */
export class VoxitElementConnectedEvent extends Event {
    /**
     * The constructor returns a newly constructed
     * VoxitElementConnectedEvent object.
     * @param {string} type - The name of the event.
     * @param {Object<string, any>} [options={}] - Optional values passed to
     *     the Event constructor.
     */
    constructor(type, options = {}) {
        super(type, { bubbles: true });
    }
}

/**
 * The VoxitStateChangeEvent class represents a voxitstatechange
 * event.  A voxitstatechange event is dispatched by the
 * <voxit-conversation> when its {@link VoxitConversationElement.state}
 * property has changed.
 */
export class VoxitStateChangeEvent extends Event {
    /**
     * The constructor returns a newly constructed VoxitStateChangeEvent
     * object.
     * @param {string} type - The name of the event.
     * @param {Object} [options={}] - Optional values passed to
     *     the Event constructor.
     * @param {string} [options.state] - The value of the state property of the
     *     <voxit-conversation>.
     */
    constructor(type, options = {}) {
        super(type);
        /** The value of the state property of the &lt;voxit-conversation&gt;. */
        this.state = options.state;
    }
}

/**
 * The VoxitAuthenticationChangeEvent class represents a
 * voxitauthenticationchange event.  A voxitauthenticationchange
 * event is dispatched by the <voxit-conversation> when its authentication state
 * has changed.
 */
export class VoxitAuthenticationChangeEvent extends Event {
    /**
     * The constructor returns a newly constructed
     * VoxitAuthenticationChangeEvent object.
     * @param {string} type - The name of the event.
     * @param {Object} [options={}] - Optional values passed to
     *     the Event constructor.
     * @param {boolean} [options.authenticated] - Whether the client is
     *     authenticated.
     * @param {boolean} [options.authenticationRequired] - Whether the
     *     server requires authentication.
     */
    constructor(type, options = {}) {
        super(type);
        /** Whether the client is authenticated. */
        this.authenticated = options.authenticated;
        /** Whether the server requires authentication. */
        this.authenticationRequired = options.authenticationRequired;
    }
}

/**
 * The VoxitConversationChangeEvent class represents a
 * voxitconversationchange event.  A voxitconversationchange event is
 * dispatched by the <voxit-conversation> when the associated conversation has
 * changed.
 */
export class VoxitConversationChangeEvent extends Event {
    /**
     * The constructor returns a newly constructed
     * VoxitConversationChangeEvent object.
     * @param {string} type - The name of the event.
     * @param {Object} [options={}] - Optional values passed to
     *     the Event constructor.
     * @param {voxitWebClient.Conversation} [options.conversation] - The
     *     conversation associated with the &lt;voxit-conversation&gt;.
     */
    constructor(type, options = {}) {
        super(type);
        /** The conversation associated with the &lt;voxit-conversation&gt;. */
        this.conversation = options.conversation;
    }
}

/**
 * The VoxitStatementsChangeEvent class represents a
 * voxitstatementschange event.  A voxitstatementschange event is
 * dispatched by the <voxit-conversation> when the statements have changed.
 */
export class VoxitStatementsChangeEvent extends Event {
    /**
     * The constructor returns a newly constructed
     * VoxitStatementsChangeEvent object.
     * @param {string} type - The name of the event.
     * @param {Object} [options={}] - Optional values passed to
     *     the Event constructor.
     * @param {Map<number, voxitWebClient.Statement>} [options.statements] - The
     *     statements associated with the &lt;voxit-conversation&gt;.
     */
    constructor(type, options = {}) {
        super(type);
        /** The statements associated with the &lt;voxit-conversation&gt;. */
        this.statements = options.statements;
    }
}

/**
 * The VoxitResultsChangeEvent class represents a voxitresultschange
 * event.  A voxitresultschange event is dispatched by the
 * <voxit-conversation> when the voting results have changed.
 */
export class VoxitResultsChangeEvent extends Event {
    /**
     * The constructor returns a newly constructed VoxitResultsChangeEvent
     * object.
     * @param {string} type - The name of the event.
     * @param {Object} [options={}] - Optional values passed to
     *     the Event constructor.
     * @param {voxitWebClient.Results} [options.results] - The voting results
     *     associated with the &lt;voxit-conversation&gt;.
     */
    constructor(type, options = {}) {
        super(type);
        /** The voting results associated with the &lt;voxit-conversation&gt;. */
        this.results = options.results;
    }
}

/**
 * The VoxitAuthenticationStartedEvent class represents a
 * voxitauthenticationstarted event.  A voxitauthenticationstarted
 * event is dispatched by the <voxit-conversation> when the authentication
 * process has been started.
 */
export class VoxitAuthenticationStartedEvent extends Event {
}

/**
 * The VoxitAuthenticationSuccessEvent class represents a
 * voxitauthenticationsuccess event.  A voxitauthenticationsuccess
 * event is dispatched by the <voxit-conversation> when the authentication
 * process was completed successfully.
 */
export class VoxitAuthenticationSuccessEvent extends Event {
}

/**
 * The VoxitAuthenticationErrorEvent class represents a
 * voxitauthenticationerror event.  A voxitauthenticationerror event
 * is dispatched by the <voxit-conversation> when the authentication process has
 * failed due to an error.
 */
export class VoxitAuthenticationErrorEvent extends Event {
    /**
     * The constructor returns a newly constructed
     * VoxitAuthenticationErrorEvent object.
     * @param {string} type - The name of the event.
     * @param {Object} [options={}] - Optional values passed to
     *     the Event constructor.
     * @param {Error} [options.error] - The error that occurred during the
     *     authentication process.
     */
    constructor(type, options = {}) {
        super(type);
        /** The error that occurred during the authentication process. */
        this.error = options.error;
    }
}

/**
 * The VoxitVoteSubmitSuccessEvent class represents a
 * voxitvotesubmitsuccess event.  A voxitvotesubmitsuccess event is
 * dispatched by the <voxit-conversation> when a vote for a statement has been
 * submitted successfully.
 */
export class VoxitVoteSubmitSuccessEvent extends Event {
    /**
     * The constructor returns a newly constructed
     * VoxitVoteSubmitSuccessEvent object.
     * @param {string} type - The name of the event.
     * @param {Object} [options={}] - Optional values passed to
     *     the Event constructor.
     * @param {string} [options.statementID] - The statement identifier.
     * @param {string} [options.value] - The statement text.
     */
    constructor(type, options = {}) {
        super(type);
        /** The statement identifier. */
        this.statementID = options.statementID;
        /** The statement text. */
        this.value = options.value;
    }
}

/**
 * The VoxitVoteSubmitErrorEvent class represents a
 * voxitvotesubmiterror event.  A voxitvotesubmiterror event is
 * dispatched by the <voxit-conversation> when a vote for a statement could not
 * be submitted due to an error.
 */
export class VoxitVoteSubmitErrorEvent extends Event {
    /**
     * The constructor returns a newly constructed
     * VoxitVoteSubmitErrorEvent object.
     * @param {string} type - The name of the event.
     * @param {Object} [options={}] - Optional values passed to
     *     the Event constructor.
     * @param {Error} [options.error] - The error that occurred when submitting
     *     the vote.
     */
    constructor(type, options = {}) {
        super(type);
        /** The error that occurred when submitting the vote. */
        this.error = options.error;
    }
}

/**
 * The VoxitStatementSubmitSuccessEvent class represents a
 * voxitstatementsubmitsuccess event.  A voxitstatementsubmitsuccess
 * event is dispatched by the <voxit-conversation> when a statement has been
 * submitted successfully.
 */
export class VoxitStatementSubmitSuccessEvent extends Event {
    /**
     * The constructor returns a newly constructed
     * VoxitStatementSubmitSuccessEvent object.
     * @param {string} type - The name of the event.
     * @param {Object} [options={}] - Optional values passed to
     *     the Event constructor.
     * @param {string} [options.value] - The statement text.
     */
    constructor(type, options = {}) {
        super(type);
        /** The statement text. */
        this.value = options.value;
    }
}

/**
 * The VoxitStatementSubmitErrorEvent class represents a
 * voxitstatementsubmiterror event.  A voxitstatementsubmiterror
 * event is dispatched by the <voxit-conversation> when a statement could not be
 * submitted due to an error.
 */
export class VoxitStatementSubmitErrorEvent extends Event {
    /**
     * The constructor returns a newly constructed
     * VoxitStatementSubmitErrorEvent object.
     * @param {string} type - The name of the event.
     * @param {Object} [options={}] - Optional values passed to
     *     the Event constructor.
     * @param {Error} [options.error] - The error that occurred when submitting
     *     the statement.
     */
    constructor(type, options = {}) {
        super(type);
        /** The error that occurred when submitting the statement. */
        this.error = options.error;
    }
}

/**
 * The VoxitNotificationsSubmitSuccessEvent class represents a
 * voxitnotificationssubmitsuccess event.  A
 * voxitnotificationssubmitsuccess event is dispatched by the
 * <voxit-conversation> when notification preferences have been submitted
 * successfully.
 */
export class VoxitNotificationsSubmitSuccessEvent extends Event {
    /**
     * The constructor returns a newly constructed
     * VoxitNotificationsSubmitSuccessEvent object.
     * @param {string} type - The name of the event.
     * @param {Object} [options={}] - Optional values passed to
     *     the Event constructor.
     * @param {boolean} [options.value] - Whether notifications are enabled.
     */
    constructor(type, options = {}) {
        super(type);
        /** Whether notifications are enabled. */
        this.value = options.value;
    }
}

/**
 * The VoxitNotificationsSubmitErrorEvent class represents a
 * voxitnotificationssubmiterror event.  A
 * voxitnotificationssubmiterror event is dispatched by the
 * <voxit-conversation> when notification preferences could not be submitted due
 * to an error.
 */
export class VoxitNotificationsSubmitErrorEvent extends Event {
    /**
     * The constructor returns a newly constructed
     * VoxitNotificationsSubmitErrorEvent object.
     * @param {string} type - The name of the event.
     * @param {Object} [options={}] - Optional values passed to
     *     the Event constructor.
     * @param {Error} [options.error] - The error that occurred when submitting
     *     the notification preferences.
     */
    constructor(type, options = {}) {
        super(type);
        /**
         * The error that occurred when submitting the notification
         * preferences.
         */
        this.error = options.error;
    }
}

/**
 * The VoxitAuthenticateEvent class represents a voxitauthenticate
 * event. A voxitauthenticate is dispatched by the <voxit-login-button>
 * when the button was interacted with.
 */
export class VoxitAuthenticateEvent extends Event {
    /**
     * The constructor returns a newly constructed VoxitAuthenticateEvent
     * object.
     * @param {string} type - The name of the event.
     * @param {Object} [options={}] - Optional values passed to
     *     the Event constructor.
     */
    constructor(type, options = {}) {
        super(type, { bubbles: true });
    }
}

/**
 * The VoxitVoteSubmitEvent class represents a voxitvotesubmit event.
 * A voxitvotesubmit is dispatched by the <voxit-vote-button> when the
 * button was interacted with.
 */
export class VoxitVoteSubmitEvent extends Event {
    /**
     * The constructor returns a newly constructed VoxitVoteSubmitEvent
     * object.
     * @param {string} type - The name of the event.
     * @param {Object} [options={}] - Optional values passed to
     *     the Event constructor.
     * @param {string} [options.statementID] - The statement identifier.
     * @param {string} [options.value] - The vote value.
     */
    constructor(type, options = {}) {
        super(type, { bubbles: true });
        /** The statement identifier. */
        this.statementID = options.statementID;
        /** The vote value. */
        this.value = options.value;
    }
}

/**
 * The VoxitStatementSubmitEvent class represents a
 * voxitstatementsubmit event. A voxitstatementsubmit is dispatched
 * by the <voxit-submit-button> when statement submission was requested.
 */
export class VoxitStatementSubmitEvent extends Event {
    /**
     * The constructor returns a newly constructed VoxitStatementSubmitEvent object.
     * @param {string} type - The name of the event.
     * @param {Object} [options={}] - Optional values passed to
     *     the Event constructor.
     * @param {string} [options.value] - The statement text.
     * @param {Element} [options.inputElement] - The input element associated
     *     with the &lt;voxit-submit-button&gt;.
     */
    constructor(type, options = {}) {
        super(type, { bubbles: true });
        /** The statement text. */
        this.value = options.value;
        /** The input element associated with the &lt;voxit-submit-button&gt;. */
        this.inputElement = options.inputElement;
    }
}

/**
 * The VoxitNotificationsSubmitEvent class represents a
 * voxitnotificationssubmit event. A voxitnotificationssubmit is
 * dispatched by the <voxit-notifications-checkbox> when the checkbox input
 * element was interacted with.
 */
export class VoxitNotificationsSubmitEvent extends Event {
    /**
     * The constructor returns a newly constructed
     * VoxitNotificationsSubmitEvent object.
     * @param {string} type - The name of the event.
     * @param {Object} [options={}] - Optional values passed to
     *     the Event constructor.
     * @param {boolean} [options.value] - Whether notifications are enabled.
     */
    constructor(type, options = {}) {
        super(type, { bubbles: true });
        /** Whether notifications are enabled. */
        this.value = options.value;
    }
}

/**
 * The VoxitConversationElement class represents a <voxit-conversation>
 * element which provides methods and properties for interacting with a
 * conversation.
 */
export class VoxitConversationElement extends HTMLElement {
    static observedAttributes = ["disabled"];
    /** @type {ElementInternals} */
    #internals;
    #childElements = [
        "voxit-topic",
        "voxit-description",
        "voxit-statement",
        "voxit-login-button",
        "voxit-vote-button",
        "voxit-submit-button",
        "voxit-notifications-checkbox",
    ];
    #state = "loading";
    #error = "";
    /** @type {voxitWebClient.ConversationClient|undefined} */
    #client;
    /** @type {?Window} */
    #loginWindow = null;
    /** @type {?number} */
    #loginWindowTimer = null;
    /** @type {?number} */
    #pollTimer = null;
    /** @type {?voxitWebClient.Statement} */
    statement = null;

    /**
     * The constructor returns a newly constructed
     * VoxitConversationElement object.
     */
    constructor() {
        super();

        const sheet = new CSSStyleSheet();
        sheet.replaceSync(`
            :host {
                display: block;
            }
            :host([hidden]) {
                display: none;
            }
        `);

        const shadow = this.attachShadow({ mode: "open" });
        shadow.adoptedStyleSheets = [sheet];
        shadow.appendChild(document.createElement("slot"));
        this.#internals = this.attachInternals();
        this.#updateInternalsStates();
    }

    /**
     * The #nonNullClient method assert the client has been initialized.
     * @returns {voxitWebClient.ConversationClient}
     */
    #nonNullClient() {
        if (typeof this.#client === "undefined" || this.#client === null) {
            throw new TypeError("client has not been initialized");
        }
        return this.#client;
    }

    #updateInternalsStates() {
        for (
            const state of [
                "loading",
                "loaded",
                "error",
                "inactive",
                "unauthenticated",
            ]
        ) {
            this.#internals.states.delete(state);
        }
        this.#internals.states.add(this.#state);
        if (this.#state === "loaded") {
            if (!this.#client?.conversation.isActive) {
                this.#internals.states.add("inactive");
            } else if (this.#client?.authenticationRequired) {
                this.#internals.states.add("unauthenticated");
            }
        }
    }

    /**
     * @param {string} state
     */
    #setState(state) {
        if (state === this.#state) {
            return;
        }
        this.#state = state;
        this.#updateChildElements(
            ...this.querySelectorAll(this.#childElements.join(",")),
        );
        this.dispatchEvent(
            new VoxitStateChangeEvent("voxitstatechange", { state }),
        );
    }

    async #init() {
        if (typeof this.#client === "undefined") {
            const base = this.getAttribute("base");
            const conversationID = this.getAttribute("conversation-id");
            if (!base || !conversationID) {
                const message = !base ? "missing base attribute" : "missing conversation-id attribute";
                this.#error = message;
                this.#setState("error");
                this.#updateInternalsStates();
                console.error("Failed to load conversation:", message);
                return;
            }
            this.#client = new voxitWebClient.ConversationClient(
                base,
                conversationID,
            );
        }

        const oldAuthenticationRequired = this.#client.authenticationRequired;
        const oldIsActive = this.#client.conversation.isActive;
        try {
            await this.#client.init();
        } catch (e) {
            this.#error = ensureError(e).message;
            this.#setState("error");
            this.#updateInternalsStates();
            console.error("Failed to load conversation:", e);
            return;
        }

        this.statement = this.#client.currentStatement() ?? null;

        if (this.#state === "loading") {
            this.addEventListener("voxitelementconnected", this);
            this.addEventListener("voxitvotesubmit", this);
            this.addEventListener("voxitstatementsubmit", this);
            this.addEventListener("voxitauthenticate", this);
            this.addEventListener("voxitnotificationssubmit", this);
            window.addEventListener("message", this);

            this.#setState("loaded");
            this.#updateInternalsStates();
        } else if (
            this.#client.authenticationRequired !== oldAuthenticationRequired ||
            this.#client.conversation.isActive !== oldIsActive
        ) {
            this.#updateInternalsStates();
            this.#updateChildElements(
                ...this.querySelectorAll(this.#childElements.join(",")),
            );
        } else {
            this.#updateChildElements(
                ...this.querySelectorAll(this.#childElements.join(",")),
            );
        }

        if (
            this.#client.conversation.isActive &&
            !this.#client.authenticationRequired
        ) {
            this.#startPoll();
        }

        console.debug(`Conversation ${this.#client.conversationID} loaded`);
    }

    connectedCallback() {
        // initialize client here since it requires access to attributes
        if (typeof this.#client === "undefined") {
            const base = this.getAttribute("base");
            const conversationID = this.getAttribute("conversation-id");
            if (!base || !conversationID) {
                const message = !base ? "missing base attribute" : "missing conversation-id attribute";
                this.#error = message;
                this.#setState("error");
                this.#updateInternalsStates();
                console.error("Failed to load conversation:", message);
                return;
            }
            this.#client = new voxitWebClient.ConversationClient(
                base,
                conversationID,
            );
            this.#init();
        }
    }

    /**
     * @param {string} name
     * @param {string} oldValue
     * @param {string} newValue
     */
    attributeChangedCallback(name, oldValue, newValue) {
        console.debug(
            `Attribute ${name} has changed from ${oldValue} to ${newValue}`,
        );
        switch (name) {
            case "disabled":
                // disabling this element also disables child elements
                for (
                    const element of /** @type {NodeListOf<VoxitButtonElement>} */(this.querySelectorAll(
                        "voxit-vote-button,voxit-vote-button,voxit-login-button",
                    ))
                ) {
                    if (element.disabled !== this.disabled) {
                        element.disabled = this.disabled;
                    }
                }
                break;
        }
    }

    /**
     * @param {Event} ev
     */
    handleEvent(ev) {
        console.debug("Event:", ev);

        if (this.state === "error") {
            return;
        }

        let evt;
        switch (ev.type) {
            case "voxitelementconnected":
                evt = /** @type {VoxitElementConnectedEvent} */(ev);
                if (evt.target instanceof Element) {
                    this.#updateChildElements(evt.target);
                }
                break;
            case "voxitauthenticate":
                this.startAuthentication();
                break;
            case "voxitvotesubmit":
                evt = /** @type {VoxitVoteSubmitEvent} */(ev);
                if (typeof evt.value !== "undefined") {
                    this.submitVote(evt.value, evt.statementID);
                }
                break;
            case "voxitstatementsubmit":
                evt = /** @type {VoxitStatementSubmitEvent} */(ev);
                if (typeof evt.value !== "undefined") {
                    this.#submitStatement(evt.value, evt.inputElement);
                }
                break;
            case "voxitnotificationssubmit":
                evt = /** @type {VoxitNotificationsSubmitEvent} */(ev);
                if (typeof evt.value !== "undefined") {
                    this.submitNotifications(evt.value);
                }
                break;
            case "message":
                evt = /** @type {MessageEvent} */(ev);
                this.#onMessage(evt);
                break;
            default:
                console.warn("Unhandled event:", ev);
        }
    }

    /**
     * Reflects the value of the baseurl HTML attribute.
     */
    get baseURL() {
        return this.#nonNullClient().baseURL;
    }

    /**
     * Reflects the value of the conversationid HTML attribute.
     */
    get conversationID() {
        return this.#nonNullClient().conversationID;
    }

    /**
     * Reflects the value of the disabled HTML attribute.
     */
    get disabled() {
        return this.hasAttribute("disabled");
    }

    /**
     * @param {any} value
     */
    set disabled(value) {
        this.toggleAttribute("disabled", Boolean(value));
    }

    /**
     * Describes the state of the conversation.
     */
    get state() {
        return this.#state;
    }

    /**
     * Describes a fatal error that occured when interacting with the server.
     */
    get error() {
        return this.#error;
    }

    /**
     * Returns the conversation client object.
     */
    get client() {
        return this.#client;
    }

    /**
     * @param  {...Element} elements
     */
    #updateChildElements(...elements) {
        for (const element of elements) {
            if (element instanceof VoxitTopicElement) {
                element.text = this.#nonNullClient().conversation.topic;
            } else if (element instanceof VoxitDescriptionElement) {
                const descriptionDocument = this.#nonNullClient().conversation.descriptionDocument;
                if (descriptionDocument) {
                    element.contentElement.replaceChildren(descriptionDocument);
                } else {
                    element.text = this.#nonNullClient().conversation.description;
                }
            } else if (element instanceof VoxitStatementElement) {
                if (this.statement !== null) {
                    element.text = this.statement.text
                    element.isMeta = this.statement.isMeta;
                    element.noStatement = false;
                } else {
                    element.text = "";
                    element.isMeta = false;
                    element.noStatement = true;
                }
            } else if (element instanceof VoxitLoginButtonElement) {
                element.inactive = !this.#nonNullClient().authenticationRequired ||
                    this.#loginWindow !== null;
            } else if (element instanceof VoxitVoteButtonElement) {
                element.inactive = this.state !== "loaded" ||
                    this.statement === null ||
                    !this.#nonNullClient().conversation.isActive ||
                    this.#nonNullClient().authenticationRequired;
            } else if (element instanceof VoxitSubmitButtonElement) {
                element.inactive = this.state !== "loaded" ||
                    !this.#nonNullClient().conversation.isActive ||
                    !this.#nonNullClient().conversation.statementsAllowed ||
                    this.#nonNullClient().authenticationRequired;
            } else if (element instanceof VoxitNotificationsCheckboxElement) {
                element.inactive = this.state !== "loaded" ||
                    !this.#nonNullClient().conversation.isActive ||
                    !this.#nonNullClient().conversation.notificationsAvailable ||
                    !this.#nonNullClient().authenticated;
                element.value =
                    this.#nonNullClient().participant.notifications.enabled;
            } else {
                console.error("failed to update unknown element:", element)
            }
        }
    }

    /**
     * The startAuthentication method starts the authentication process by
     * opening a popup window which redirects the user to the OpenID Connect
     * identity provider.
     */
    startAuthentication() {
        if (this.#loginWindow !== null) {
            return;
        }

        const width = 600;
        const height = 600;
        const left = Math.max(window.screen.width - width, 0) / 2;
        const top = Math.max(window.screen.height - height, 0) / 2;
        this.#loginWindow = window.open(
            this.#nonNullClient().loginURL,
            "voxit-login",
            `popup width=${width} height=${height} top=${top} left=${left}`,
        );
        if (this.#loginWindow === null) {
            return;
        }

        // check if window has been closed without successful authentication
        this.#loginWindowTimer = setInterval(
            this.#onLoginWindowTimer.bind(this),
            1000,
        );

        this.#updateChildElements(
            ...this.querySelectorAll("voxit-login-button"),
        );

        this.dispatchEvent(
            new VoxitAuthenticationStartedEvent(
                "voxitauthenticationstarted",
            ),
        );
    }

    /**
     * @param {MessageEvent} ev
     */
    async #onMessage(ev) {
        if (
            this.#loginWindow === null ||
            ev.origin !== this.#nonNullClient().baseURL.origin
        ) {
            return;
        }

        clearInterval(this.#loginWindowTimer ?? undefined);
        this.#loginWindowTimer = null;
        this.#loginWindow.close();
        this.#loginWindow = null;

        if (typeof ev.data === "string" && ev.data !== "") {
            const error = new Error(
                `Unexpected message "${ev.data}" from login window`,
            );
            this.dispatchEvent(
                new VoxitAuthenticationErrorEvent(
                    "voxitauthenticationerror",
                    { error },
                ),
            );
            console.error(error.message);
            return;
        }

        // reinitialize the client
        await this.#init();
        this.dispatchEvent(
            new VoxitAuthenticationSuccessEvent(
                "voxitauthenticationsuccess",
            ),
        );
    }

    #onLoginWindowTimer() {
        if (this.#loginWindow === null || this.#loginWindow.closed) {
            clearInterval(this.#loginWindowTimer ?? undefined);

            this.#loginWindowTimer = null;
            this.#loginWindow = null;

            this.#updateChildElements(
                ...this.querySelectorAll("voxit-login-button"),
            );

            const error = new Error("Authentication process cancelled by user");
            this.dispatchEvent(
                new VoxitAuthenticationErrorEvent(
                    "voxitauthenticationerror",
                    { error },
                ),
            );
            console.debug(error.message);
        }
    }

    /**
     * @param {Error} error
     */
    #handleCommonRequestErrors(error) {
        if (error instanceof voxitWebClient.AuthenticationRequiredError) {
            this.#stopPoll();
            this.#updateInternalsStates();
            this.#updateChildElements(
                ...this.querySelectorAll(this.#childElements.join(",")),
            );
            this.dispatchEvent(
                new VoxitAuthenticationChangeEvent(
                    "voxitauthenticationchange",
                    {
                        authenticated: this.#nonNullClient().authenticated,
                        authenticationRequired:
                            this.#nonNullClient().authenticationRequired,
                    },
                ),
            );
        } else if (error instanceof voxitWebClient.ConversationInactiveError) {
            this.#stopPoll();
            this.#updateInternalsStates();
            this.#updateChildElements(
                ...this.querySelectorAll(this.#childElements.join(",")),
            );
            this.dispatchEvent(
                new VoxitConversationChangeEvent(
                    "voxitconversationchange",
                    { conversation: this.#nonNullClient().conversation },
                ),
            );
        }
    }

    /**
     * The submitVote method submits a vote for a statement.
     * @param {string} value - Value representing the vote.
     * @param {?string} [statementID=null] - The statement identifier of the
     *     statement voted on or null for the current statement.
     */
    async submitVote(value, statementID = null) {
        // statement ID is optional, fall back to the current statement
        statementID = statementID ?? this.statement?.id.toString();
        if (typeof statementID === "undefined") {
            return;
        }

        let vote;
        switch (value) {
            case "agree":
                vote = voxitWebClient.Vote.Agree;
                break;
            case "disagree":
                vote = voxitWebClient.Vote.Disagree;
                break;
            case "neutral":
                vote = voxitWebClient.Vote.Neutral;
                break;
            default:
                throw new TypeError("Received invalid vote value.");
                return;
        }

        try {
            await this.#nonNullClient().addVote(statementID, vote);
        } catch (e) {
            const error = ensureError(e);
            this.dispatchEvent(
                new VoxitVoteSubmitErrorEvent(
                    "voxitvotesubmiterror",
                    { error },
                ),
            );
            this.#handleCommonRequestErrors(error);
            console.error("Failed to submit vote:", e);
            return;
        }

        this.statement = this.#nonNullClient().currentStatement() ?? null;
        this.#updateChildElements(
            ...this.querySelectorAll("voxit-statement,voxit-vote-button"),
        );
        this.dispatchEvent(
            new VoxitVoteSubmitSuccessEvent(
                "voxitvotesubmitsuccess",
                { statementID, value },
            ),
        );
    }

    /**
     * @param {string} value
     * @param {Element=} inputElement
     */
    async #submitStatement(value, inputElement) {
        if (typeof value === "undefined") {
            throw new TypeError("The statement value is missing.");
        }

        value = value.trim();

        try {
            await this.#nonNullClient().addStatement(value);
        } catch (e) {
            if (e instanceof voxitWebClient.StatementsNotAllowedError) {
                this.#updateChildElements(
                    ...this.querySelectorAll("voxit-submit-button"),
                );
                this.dispatchEvent(
                    new VoxitStatementSubmitErrorEvent(
                        "voxitstatementsubmiterror",
                        { error: e },
                    ),
                );
                this.dispatchEvent(
                    new VoxitConversationChangeEvent(
                        "voxitconversationchange",
                        { conversation: this.#nonNullClient().conversation },
                    ),
                );
                console.debug(e.message);
            } else {
                const error = ensureError(e);
                this.dispatchEvent(
                    new VoxitStatementSubmitErrorEvent(
                        "voxitstatementsubmiterror",
                        { error },
                    ),
                );
                this.#handleCommonRequestErrors(error);
                console.error("Failed to submit statement:", error);
            }
            return;
        }

        if (typeof inputElement !== "undefined" && "value" in inputElement) {
            inputElement.value = "";
        }
        this.dispatchEvent(
            new VoxitStatementSubmitSuccessEvent(
                "voxitstatementsubmitsuccess",
                { value },
            ),
        );
    }

    /**
     * The submitStatement method submits a new statement.
     * @param {string} value - The statement text.
     * @returns {Promise<void>}
     */
    async submitStatement(value) {
        return this.#submitStatement(value);
    }

    /**
     * The submitNotifications method submits notifications preferences.
     * @param {boolean} value - Whether notifications are enabled.
     * @returns {Promise<void>}
     */
    async submitNotifications(value) {
        if (typeof value === "undefined") {
            throw new TypeError("The value is missing.");
        }

        try {
            await this.#nonNullClient().setNotifications(value);
        } catch (e) {
            if (
                (e instanceof voxitWebClient.NotificationsNotAvailableError) ||
                (e instanceof voxitWebClient.EmailAddressMissingError)
            ) {
                this.#updateChildElements(
                    ...this.querySelectorAll("voxit-notifications-checkbox"),
                );
                this.dispatchEvent(
                    new VoxitNotificationsSubmitErrorEvent(
                        "voxitnotificationssubmiterror",
                        { error: e },
                    ),
                );
                this.dispatchEvent(
                    new VoxitConversationChangeEvent(
                        "voxitconversationchange",
                        { conversation: this.#nonNullClient().conversation },
                    ),
                );
                console.debug(e.message);
            } else {
                const error = ensureError(e);
                this.dispatchEvent(
                    new VoxitNotificationsSubmitErrorEvent(
                        "voxitnotificationssubmiterror",
                        { error },
                    ),
                );
                this.#handleCommonRequestErrors(error);
                console.error(
                    "Failed to submit notifications subscription:",
                    error,
                );
            }
            return;
        }

        this.dispatchEvent(
            new VoxitNotificationsSubmitSuccessEvent(
                "voxitnotificationssubmitsuccess",
                { value },
            ),
        );
    }

    async #onPollTimer() {
        this.#pollTimer = null;

        const [statementsResult, resultsResult] = await Promise.allSettled([
            this.#nonNullClient().pollStatements(),
            this.#nonNullClient().pollResults(),
        ]);
        const statementsChanged = statementsResult.status === "fulfilled" ? statementsResult.value : false;
        let resultsChanged = resultsResult.status === "fulfilled" ? resultsResult.value : false;
        if (
            (
                resultsResult.status === "rejected" &&
                resultsResult.reason instanceof voxitWebClient.AuthenticationRequiredError
            ) ||
            (
                statementsResult.status === "rejected" &&
                statementsResult.reason instanceof voxitWebClient.AuthenticationRequiredError
            )
        ) {
            this.#updateChildElements(
                ...this.querySelectorAll(this.#childElements.join(",")),
            );
            this.dispatchEvent(
                new VoxitAuthenticationChangeEvent(
                    "voxitauthenticationchange",
                    {
                        authenticated: this.#nonNullClient().authenticated,
                        authenticationRequired:
                            this.#nonNullClient().authenticationRequired,
                    },
                ),
            );
        } else if (
            resultsResult.status === "rejected" &&
            resultsResult.reason instanceof voxitWebClient.ResultsNotAvailableError
        ) {
            resultsChanged = true;
        } else {
            if (resultsResult.status === "rejected") {
                console.error("Failed to poll results:", resultsResult.reason);
            }
            if (statementsResult.status === "rejected") {
                console.error("Failed to poll statements:", statementsResult.reason);
            }
        }

        if (
            this.#nonNullClient().conversation.isActive &&
            !this.#nonNullClient().authenticationRequired
        ) {
            this.#startPoll();
        }

        if (statementsChanged) {
            if (this.statement === null) {
                // all statements had already been voted on, update the current statement
                this.statement = this.#nonNullClient().currentStatement() ?? null;
                this.#updateChildElements(
                    ...this.querySelectorAll("voxit-statement,voxit-vote-button"),
                );
            }
            this.dispatchEvent(
                new VoxitStatementsChangeEvent(
                    "voxitstatementschange",
                    { statements: this.#nonNullClient().statements },
                ),
            );
        }
        if (resultsChanged) {
            this.dispatchEvent(
                new VoxitResultsChangeEvent("voxitresultschange", {
                    results: this.#nonNullClient().results,
                }),
            );
        }
    }

    #startPoll() {
        if (this.#pollTimer !== null) {
            return;
        }
        const minCeiled = Math.ceil(POLL_INTERVAL_MIN);
        const maxFloored = Math.floor(POLL_INTERVAL_MAX);
        const interval = Math.floor(
            Math.random() * (maxFloored - minCeiled) + minCeiled,
        );
        this.#pollTimer = setTimeout(this.#onPollTimer.bind(this), interval);
    }

    #stopPoll() {
        if (this.#pollTimer === null) {
            return;
        }
        clearTimeout(this.#pollTimer);
        this.#pollTimer = null;
    }
}

/**
 * The VoxitContentElement represents an abstract custom element for
 * displaying text content.
 */
export class VoxitContentElement extends HTMLElement {
    contentElement;

    /**
     * The constructor returns a newly constructed VoxitContentElement
     * object.
     */
    constructor() {
        super();

        const sheet = new CSSStyleSheet();
        sheet.replaceSync(`
            :host {
                display: inline;
            }
            :host([hidden]) {
                display: none;
            }
        `);

        const shadow = this.attachShadow({ mode: "open" });
        shadow.adoptedStyleSheets = [sheet];
        this.contentElement = document.createElement("span");
        this.contentElement.id = "content";
        shadow.appendChild(this.contentElement);
    }

    connectedCallback() {
        customElements.whenDefined("voxit-conversation").then(() => {
            this.dispatchEvent(
                new VoxitElementConnectedEvent(
                    "voxitelementconnected",
                ),
            );
        });
    }

    /**
     * The text content of the element.
     */
    get text() {
        return this.contentElement.textContent;
    }

    /**
     * @param {?string} value
     */
    set text(value) {
        this.contentElement.textContent = value;
    }
}

/**
 * The VoxitTopicElement class represents an <voxit-topic> element which
 * displays the conversation topic.
 */
export class VoxitTopicElement extends VoxitContentElement {}

/**
 * The VoxitDescriptionElement class represents an <voxit-description> element which
 * displays the conversation description.
 */
export class VoxitDescriptionElement extends VoxitContentElement {}

/**
 * The VoxitStatementElement class represents an <voxit-statement> element which
 * displays the text of the current statement.
 */
export class VoxitStatementElement extends VoxitContentElement {
    #internals;

    /**
     * The constructor returns a newly constructed VoxitContentElement
     * object.
     */
    constructor() {
        super();

        const sheet = new CSSStyleSheet();
        sheet.replaceSync(`
            #no-statement {
                display: none;
            }

            :host(:state(no-statement)) #no-statement {
                display: inherit;
            }
        `);

        const noStatementElement = document.createElement("span");
        noStatementElement.id = "no-statement";
        noStatementElement.appendChild(document.createElement("slot"));
        if (typeof this.shadowRoot === "undefined" || this.shadowRoot === null) {
            throw new TypeError("shadow root has not been attached");
        }
        this.shadowRoot.appendChild(noStatementElement);
        this.shadowRoot.adoptedStyleSheets.push(sheet);
        this.#internals = this.attachInternals();
        this.#internals.states.add("no-statement");
    }

    get noStatement() {
        return this.#internals.states.has("no-statement");
    }

    /**
     * @param {any} value
     */
    set noStatement(value) {
        if (value) {
            this.#internals.states.add("no-statement");
        } else {
            this.#internals.states.delete("no-statement");
        }
    }

    /**
     * Whether the current statement is marked as meta.
     */
    get isMeta() {
        return this.#internals.states.has("is-meta");
    }

    /**
     * @param {any} value
     */
    set isMeta(value) {
        if (value) {
            this.#internals.states.add("is-meta");
        } else {
            this.#internals.states.delete("is-meta");
        }
    }
}

/**
 * The VoxitButtonElement represents an abstract custom button element.
 */
export class VoxitButtonElement extends HTMLElement {
    static observedAttributes = ["disabled"];
    #internals;
    #inactive = true;
    #buttonElement;

    /**
     * The constructor returns a newly constructed VoxitButtonElement
     * object.
     */
    constructor() {
        super();

        const sheet = new CSSStyleSheet();
        sheet.replaceSync(`
            :host {
                display: inline;
            }
            :host([hidden]) {
                display: none;
            }
        `);
        this.#buttonElement = document.createElement("button");
        this.#buttonElement.part.add("button");
        this.#buttonElement.appendChild(document.createElement("slot"));
        const shadow = this.attachShadow({ mode: "open" });
        shadow.adoptedStyleSheets = [sheet];
        shadow.appendChild(this.#buttonElement);
        this.#internals = this.attachInternals();
        this.addEventListener("click", this);
    }

    connectedCallback() {
        customElements.whenDefined("voxit-conversation").then(() => {
            this.dispatchEvent(
                new VoxitElementConnectedEvent(
                    "voxitelementconnected",
                ),
            );
        });
    }

    /**
     * @param {string} name
     * @param {string} oldValue
     * @param {string} newValue
     */
    attributeChangedCallback(name, oldValue, newValue) {
        console.debug(
            `Attribute ${name} has changed from ${oldValue} to ${newValue}`,
        );
        switch (name) {
            case "disabled":
                this.#buttonElement.disabled = this.disabled;
                break;
        }
    }

    /**
     * @param {Event} ev
     */
    handleEvent(ev) {
    }

    /**
     * Reflects the disabled HTML attribute of the element.
     */
    get disabled() {
        return this.hasAttribute("disabled");
    }

    /**
     * @param {any} value
     */
    set disabled(value) {
        this.toggleAttribute("disabled", Boolean(value));
    }

    /**
     * Reflects the inactive custom state.
     */
    get inactive() {
        return this.#inactive;
    }

    /**
     * @param {any} value
     */
    set inactive(value) {
        this.#inactive = value;
        this.#buttonElement.disabled = value || this.disabled;
        if (value) {
            this.#internals.states.add("inactive");
        } else {
            this.#internals.states.delete("inactive");
        }
    }
}

/**
 * The VoxitLoginButtonElement class represents an <voxit-login-button>
 * element which provides a button for starting the authentication process.
 */
export class VoxitLoginButtonElement extends VoxitButtonElement {
    /**
     * @param {Event} ev
     * @override
     */
    handleEvent(ev) {
        switch (ev.type) {
            case "click":
                this.dispatchEvent(
                    new VoxitAuthenticateEvent("voxitauthenticate"),
                );
                break;
        }
    }
}

/**
 * The VoxitVoteButtonElement class represents an <voxit-vote-button> element
 * which provides a button for voting for the current statement.
 */
export class VoxitVoteButtonElement extends VoxitButtonElement {
    /** @override */
    static observedAttributes = [...super.observedAttributes, "type"];
    #type = "neutral";

    /**
     * @param {string} name
     * @param {string} oldValue
     * @param {string} newValue
     * @override
     */
    attributeChangedCallback(name, oldValue, newValue) {
        console.debug(
            `Attribute ${name} has changed from ${oldValue} to ${newValue}`,
        );
        switch (name) {
            case "type":
                this.type = newValue;
                return;
        }
        super.attributeChangedCallback(name, oldValue, newValue);
    }

    /**
     * @param {Event} ev
     * @override
     */
    handleEvent(ev) {
        switch (ev.type) {
            case "click":
                this.dispatchEvent(
                    new VoxitVoteSubmitEvent("voxitvotesubmit", {
                        value: this.#type,
                    }),
                );
                break;
        }
    }

    /**
     * Reflects the value of the type HTML attribute.
     */
    get type() {
        return this.#type;
    }

    /**
     * @param {any} value
     */
    set type(value) {
        if (this.#type === value || !this.#isTypeValid(value)) {
            return;
        }
        this.#type = value;

        if (this.getAttribute("type") === value) {
            return;
        }
        this.setAttribute("type", value);
    }

    /**
     * @param {string} type
     * @returns {boolean}
     */
    #isTypeValid(type) {
        switch (type) {
            case "agree":
            case "disagree":
            case "neutral":
                return true;
        }
        return false;
    }
}

/**
 * The VoxitSubmitButtonElement class represents an <voxit-submit-button> element
 * which provides a button for submitting a statement.
 */
export class VoxitSubmitButtonElement extends VoxitButtonElement {
    /**
     *
     * @param {Event} ev
     * @override
     */
    handleEvent(ev) {
        switch (ev.type) {
            case "click":
                if (this.control === null) {
                    return;
                }
                const element = /** @type {HTMLInputElement|HTMLTextAreaElement} */(this.control);
                this.dispatchEvent(
                    new VoxitStatementSubmitEvent(
                        "voxitstatementsubmit",
                        {
                            "value": element.value,
                            inputElement: element,
                        },
                    ),
                );
                break;
        }
    }

    /**
     * Reflects the value of the submitfor HTML attribute.
     */
    get submitFor() {
        return this.getAttribute("submitfor") ?? "";
    }

    /**
     * @type {string}
     */
    set submitFor(value) {
        this.setAttribute("submitfor", value);
    }

    /**
     * A reference to the input element with which the <voxit-submit-button> is
     * associated or null if there is not associated element.
     */
    get control() {
        const id = this.getAttribute("submitfor");
        if (id === null) {
            return null;
        }
        const element = document.getElementById(id);
        if (!(element instanceof HTMLInputElement) && !(element instanceof HTMLTextAreaElement)) {
            return null;
        }
        return element;
    }

    /**
     * @type {HTMLElement|null}
     */
    set control(value) {
        if (!(value instanceof HTMLInputElement) && !(value instanceof HTMLTextAreaElement)) {
            return;
        }
        if (value.id === "") {
            return;
        }
        if (!("value" in value)) {
            return;
        }
        this.setAttribute("submitfor", value.id);
    }
}

/**
 * The VoxitNotificationsCheckboxElement class represents an
 * <voxit-notifications-checkbox> element which provides a checkbox input for
 * enabling or disabling notifications.
 */
export class VoxitNotificationsCheckboxElement extends HTMLElement {
    static observedAttributes = ["disabled"];
    static formAssociated = true;
    #checkboxElement;
    #internals;
    #inactive = true;

    /**
     * The constructor returns a newly constructed
     * VoxitNotificationsCheckboxElement object.
     */
    constructor() {
        super();

        const sheet = new CSSStyleSheet();
        sheet.replaceSync(`
            :host {
                display: inline;
            }
            :host([hidden]) {
                display: none;
            }
        `);
        this.#checkboxElement = document.createElement("input");
        this.#checkboxElement.type = "checkbox";
        this.#checkboxElement.part.add("checkbox");
        // click is handled outside the shadow root to allow for label support
        this.#checkboxElement.addEventListener(
            "click",
            (ev) => ev.preventDefault(),
        );
        const shadow = this.attachShadow({ mode: "open" });
        shadow.adoptedStyleSheets = [sheet];
        shadow.appendChild(this.#checkboxElement);
        this.#internals = this.attachInternals();
        this.addEventListener("click", this);
    }

    connectedCallback() {
        customElements.whenDefined("voxit-conversation").then(() => {
            this.dispatchEvent(
                new VoxitElementConnectedEvent(
                    "voxitelementconnected",
                ),
            );
        });
    }

    /**
     * @param {string} name
     * @param {string} oldValue
     * @param {string} newValue
     */
    attributeChangedCallback(name, oldValue, newValue) {
        console.debug(
            `Attribute ${name} has changed from ${oldValue} to ${newValue}`,
        );
        switch (name) {
            case "disabled":
                this.#checkboxElement.disabled = this.disabled;
                break;
        }
    }

    /**
     * @param {Event} ev
     */
    handleEvent(ev) {
        switch (ev.type) {
            case "click":
                this.dispatchEvent(
                    new VoxitNotificationsSubmitEvent(
                        "voxitnotificationssubmit",
                        {
                            value: this.#checkboxElement.checked,
                        },
                    ),
                );
                break;
        }
    }

    /**
     * Reflects the value of the disabled HTML attribute.
     */
    get disabled() {
        return this.hasAttribute("disabled");
    }

    /**
     * @param {any} value
     */
    set disabled(value) {
        this.toggleAttribute("disabled", Boolean(value));
    }

    /**
     * Reflects the inactive custom state.
     */
    get inactive() {
        return this.#inactive;
    }

    /**
     * @param {any} value
     */
    set inactive(value) {
        this.#inactive = value;
        this.#checkboxElement.disabled = value || this.disabled;
        this.#checkboxElement.indeterminate = value;
        if (value) {
            this.#internals.states.add("inactive");
        } else {
            this.#internals.states.delete("inactive");
        }
    }

    /**
     * Whether the checkbox is checked.
     */
    get value() {
        return this.#checkboxElement.checked;
    }

    /**
     * @param {any} value
     */
    set value(value) {
        this.#checkboxElement.checked = value;
    }
}

window.customElements.define("voxit-topic", VoxitTopicElement);
window.customElements.define("voxit-description", VoxitDescriptionElement);
window.customElements.define("voxit-statement", VoxitStatementElement);
window.customElements.define("voxit-login-button", VoxitLoginButtonElement);
window.customElements.define("voxit-vote-button", VoxitVoteButtonElement);
window.customElements.define("voxit-submit-button", VoxitSubmitButtonElement);
window.customElements.define(
    "voxit-notifications-checkbox",
    VoxitNotificationsCheckboxElement,
);
window.customElements.define("voxit-conversation", VoxitConversationElement);
